import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { cn } from '../../../utils/cn';

const AppointmentRequestModal = ({ 
  user, 
  timeSlot, 
  isOpen, 
  onClose, 
  onSubmit 
}) => {
  const [formData, setFormData] = useState({
    requesterName: 'João Silva', // Auto-filled from current user
    appointmentType: '',
    preferredTime: '',
    message: '',
    urgency: 'normal'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const appointmentTypes = [
    { value: 'mentoria', label: 'Mentoria Espiritual' },
    { value: 'aconselhamento', label: 'Aconselhamento' },
    { value: 'estudo_biblico', label: 'Estudo Bíblico' },
    { value: 'oracao', label: 'Oração' },
    { value: 'orientacao', label: 'Orientação Geral' },
    { value: 'outro', label: 'Outro' }
  ];

  const urgencyOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'urgente', label: 'Urgente' },
    { value: 'flexivel', label: 'Flexível' }
  ];

  useEffect(() => {
    if (timeSlot && isOpen) {
      const formattedTime = timeSlot?.date && timeSlot?.time 
        ? `${format(timeSlot?.date, 'dd/MM/yyyy')} às ${timeSlot?.time}`
        : '';
      
      setFormData(prev => ({
        ...prev,
        preferredTime: formattedTime
      }));
    }
    setErrors({});
  }, [timeSlot, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.appointmentType) {
      newErrors.appointmentType = 'Tipo de atendimento é obrigatório';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Mensagem é obrigatória';
    }

    if (formData?.message && formData?.message?.length < 10) {
      newErrors.message = 'Mensagem deve ter pelo menos 10 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      try {
        const requestData = {
          ...formData,
          userId: user?.id,
          userName: user?.name,
          requestedSlot: timeSlot,
          timestamp: new Date()?.toISOString()
        };
        
        await onSubmit(requestData);
      } catch (error) {
        console.error('Error submitting request:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-modal transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="availability-form relative w-full max-w-md bg-card rounded-lg shadow-tier-3 border border-border">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                Solicitar Atendimento
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {user?.name} • {user?.title}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              iconName="X"
              className="h-6 w-6"
            />
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Requester Name (read-only) */}
            <div>
              <Input
                label="Seu Nome"
                value={formData?.requesterName}
                disabled
                className="bg-muted/50"
              />
            </div>

            {/* Appointment Type */}
            <div>
              <Select
                label="Tipo de Atendimento"
                value={formData?.appointmentType}
                onChange={(value) => handleInputChange('appointmentType', value)}
                options={appointmentTypes}
                placeholder="Selecione o tipo de atendimento"
                error={errors?.appointmentType}
                required
              />
            </div>

            {/* Preferred Time */}
            <div>
              <Input
                label="Horário Sugerido"
                value={formData?.preferredTime}
                onChange={(e) => handleInputChange('preferredTime', e?.target?.value)}
                placeholder="Ex: 28/01/2025 às 10:00"
                description="Baseado no horário selecionado no calendário"
              />
            </div>

            {/* Urgency */}
            <div>
              <Select
                label="Urgência"
                value={formData?.urgency}
                onChange={(value) => handleInputChange('urgency', value)}
                options={urgencyOptions}
                placeholder="Selecione a urgência"
              />
            </div>

            {/* Message */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Mensagem Opcional
                <span className="text-destructive ml-1">*</span>
              </label>
              <textarea
                value={formData?.message}
                onChange={(e) => handleInputChange('message', e?.target?.value)}
                placeholder="Descreva brevemente o motivo do atendimento, suas necessidades ou questões específicas..."
                className={cn(
                  "w-full min-h-[100px] px-3 py-2 text-sm border border-input rounded-md resize-none",
                  "bg-white text-black placeholder:text-muted-foreground",
                  "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                  errors?.message && "border-destructive focus:ring-destructive"
                )}
              />
              {errors?.message && (
                <p className="text-sm text-destructive mt-1">
                  {errors?.message}
                </p>
              )}
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-muted-foreground">
                  Mínimo 10 caracteres
                </p>
                <span className="text-xs text-muted-foreground">
                  {formData?.message?.length}/500
                </span>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="text-sm">
                  <p className="font-medium text-primary mb-1">
                    Como funciona?
                  </p>
                  <p className="text-primary/80">
                    Sua solicitação será enviada para {user?.name}. Você receberá uma resposta em até 24 horas com a confirmação ou proposta de novo horário.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/90"
                iconName="Send"
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Solicitar Horário
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppointmentRequestModal;